import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";
import type { PaginatedMeta } from "./audience_helper";

const apiBase = config.api.API_URL;

export type CampaignSendStatus =
  | "queued"
  | "pending_approval"
  | "preparing"
  | "sending"
  | "completed"
  | "rejected"
  | "cancelled"
  | "failed"
  | "paused";

export type CampaignSend = {
  id: number;
  campaign_id: number;
  created_by_user_id?: number | null;
  approved_by_user_id?: number | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  created_by?: { id: number; name: string } | null;
  approved_by?: { id: number; name: string } | null;
  campaign?: { id: number; name: string } | null;
  status: CampaignSendStatus;
  /** canary | circuit | platform | deadline — copy leiga distinta. */
  pause_reason?: "canary" | "circuit" | "platform" | "deadline" | string | null;
  /** Quantas pausas circuit/platform neste disparo. */
  circuit_pause_count?: number;
  /** true = 2ª+ pausa — só suporte/SA retoma. */
  needs_sa_resume?: boolean;
  /** 1ª pausa: ISO UTC em que o job pode auto-retomar. */
  circuit_auto_resume_eta_at?: string | null;
  /** >0 + sending = piloto em verificação. */
  canary_pilot_size?: number | null;
  is_test: boolean;
  scheduled_at?: string | null;
  /** 1 e-mail a cada N segundos (0 = lote normal). */
  send_interval_seconds?: number;
  /** Data limite da promo — após isso pausa com pause_reason=deadline. */
  deadline_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  subject: string;
  from_email: string;
  from_name: string;
  reply_to?: string | null;
  audience?: {
    mode?: string;
    tag_ids?: number[];
    email?: string;
    filter?: unknown;
    segment_id?: unknown;
    partial_sample?: boolean;
    eligible_total?: number;
    selected_count?: number;
    /** random | engagement — LAC-115 */
    sample_by?: "random" | "engagement" | string;
    tag_excluded?: boolean;
    resume_remainder_tomorrow?: boolean;
    excluded_tag_id?: number;
    excluded_tag_name?: string;
    excluded_count?: number;
    excluded_tagged?: number;
    excluded_tag_missing?: boolean;
    remainder_over_daily_cap?: boolean;
    daily_cap_for_remainder?: number;
    continuation_will_partial?: boolean;
    continuation_chain_capped?: boolean;
    continuation_send_id?: number;
    continuation_of_send_id?: number;
    resume_scheduled_at?: string;
    partial_root_send_id?: number;
    partial_day_index?: number;
    partial_days_estimated?: number;
    partial_progress_label?: string;
    partial_max_continuation_days?: number;
    partial_chain_capped?: boolean;
    coverage_bps?: number;
    coverage_sent?: number;
    coverage_total?: number;
    suggest_raise_daily_cap?: boolean;
    partial_chain?: {
      root_send_id: number;
      nodes: Array<{
        id: number;
        day_index: number;
        status: string;
        pause_reason?: string | null;
        sent: number;
        queued: number;
        scheduled_at?: string | null;
      }>;
      cancellable_count: number;
      already_sent_total: number;
      terminal_count: number;
      coverage_bps: number;
      coverage_sent: number;
      coverage_total: number;
      chain_paused: boolean;
      suggest_raise_daily_cap: boolean;
    };
  };
  /** Total deste disparo na prepare — não diminui; o restante é pending_count. */
  queued: number;
  sent: number;
  opened: number;
  /**
   * Aberturas só no pixel (MPP/prefetch) ainda sem confirmação —
   * não entram no KPI `opened` (confirmadas).
   */
  provisional_opened?: number;
  clicked: number;
  bounced: number;
  /** Bounce permanente (endereço inválido) — subset de bounced. */
  hard_bounced?: number;
  /** Bounce temporário (caixa cheia etc.) — subset de bounced. */
  soft_bounced?: number;
  complained: number;
  unsubscribed: number;
  failed: number;
  /** Destinatários ainda pending (fila justa). */
  pending_count?: number;
  /** p1 = pequeno/canary; p2 = grande — ops/UI leiga. */
  priority_band?: "p1" | "p2" | string | null;
  /** true = parado porque a capacidade do dia da plataforma acabou (LAC-096). */
  waiting_for_capacity?: boolean;
  /** Taxas % 0–100 (DOC 06 § 6.4 · LAC-035). */
  open_rate?: number;
  click_rate?: number;
  ctr?: number;
  ctor?: number;
  bounce_rate?: number;
  hard_bounce_rate?: number;
  soft_bounce_rate?: number;
  complaint_rate?: number;
  delivery_rate?: number;
  unsubscribe_rate?: number;
  /** Conversões leves (clique em link meta) — F2. */
  goal_count?: number;
  /** Links trackeados do disparo (DOC 14 § 9 · LAC-049). */
  links?: Array<{
    id: number;
    url: string;
    click_count: number;
    is_goal?: boolean;
  }>;
  html_snapshot?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type CampaignSendRecipientStatus =
  | "pending"
  | "sent"
  | "skipped"
  | "bounced"
  | "complained"
  | "failed";

/** hard = inválido; soft = temporário — copy leiga na UI (não jargão SES). */
export type BounceKind = "hard" | "soft";

export type BounceKindFilter = BounceKind | "";

export type CampaignSendRecipient = {
  id: number;
  campaign_send_id: number;
  contact_id: number | null;
  email: string;
  status: CampaignSendRecipientStatus;
  /** Presente só quando status=bounced; null nos demais. */
  bounce_kind?: BounceKind | null;
  provider_message_id?: string | null;
  sent_at?: string | null;
  /** Engajamento neste disparo (email_events) — LAC-117. */
  opened?: boolean;
  clicked?: boolean;
  opened_at?: string | null;
  clicked_at?: string | null;
};

export type RecipientEngagementFilter = "opened" | "clicked" | "";

export type SendStats = {
  period_days: number;
  from: string;
  to: string;
  sends_count: number;
  queued: number;
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
  failed: number;
  open_rate: number;
  click_rate: number;
  ctr: number;
  ctor: number;
  bounce_rate: number;
  /** Taxa de bounce permanente (inválido) — banner no dashboard. */
  hard_bounce_rate?: number;
  soft_bounce_rate?: number;
  complaint_rate: number;
  delivery_rate: number;
  unsubscribe_rate: number;
  /** Limiar % do aviso Atenção/KPI (por conta; default 5) — LAC-191. */
  bounce_rate_warn_threshold_percent?: number;
};

export type SendCampaignPayload = {
  audience: {
    mode: "all" | "tags" | "segment" | "filter";
    tag_ids?: number[];
    segment_id?: number | null;
    filter?: {
      status?: string;
      tag_ids?: number[];
      tag_match?: "and" | "or";
      fields?: Record<
        string,
        | string
        | {
            op: "eq" | "contains" | "gte" | "lte" | "between" | "in";
            value: string | string[];
            value_to?: string;
          }
      >;
    };
  };
  /** Amostra limitada ao restante de hoje / cota. */
  partial_sample?: boolean;
  /** Prioriza quem abriu/clicou (padrão) ou aleatório. */
  sample_by?: "random" | "engagement";
  /** Tag automática nos que ficaram de fora (padrão true no backend). */
  tag_excluded?: boolean;
  /** Agenda disparo-filho amanhã com a tag dos excluídos. */
  resume_remainder_tomorrow?: boolean;
  /** Horário de parede (fuso da conta) para o restante. */
  resume_scheduled_at?: string | null;
  /** Tag existente para marcar excluídos (senão cria automática). */
  excluded_tag_id?: number | null;
  scheduled_at?: string | null;
  /** 1 e-mail a cada N segundos (0 = mais rápido possível). */
  send_interval_seconds?: number | null;
  /** Data limite da promo (mesmo formato do scheduled_at). */
  deadline_at?: string | null;
};

export type TestCampaignPayload = {
  email: string;
};

export type QueueSendResponse = ApiSuccess<{
  id: number;
  status: string;
  job_status: string;
  is_test?: boolean;
  scheduled_at?: string | null;
}>;

type SendsListResponse = ApiSuccess<CampaignSend[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

/** Histórico paginado de disparos (GET /sends). */
export const getSends = (params?: {
  page?: number;
  per_page?: number;
  /** Filtro opcional (ex.: paused) — GET /sends?status= */
  status?: CampaignSendStatus | string;
}): Promise<SendsListResponse> => {
  return axios.get(apiBase + "/sends", { params });
};

type PendingApprovalsListResponse = ApiSuccess<CampaignSend[]> & {
  meta: PaginatedMeta;
};

/**
 * Caixa do aprovador: envios em `pending_approval`.
 * Rota: GET /api/v1/sends/pending-approval (F2).
 */
export const getPendingApprovals = (params?: {
  page?: number;
  per_page?: number;
}): Promise<PendingApprovalsListResponse> => {
  return axios.get(apiBase + "/sends/pending-approval", { params });
};

export const getSend = (id: number): Promise<ApiSuccess<CampaignSend>> => {
  return axios.get(apiBase + "/sends/" + id);
};

/** Agregado de taxas do tenant (default 7d) — GET /sends/stats. */
export const getSendStats = (params?: {
  days?: number;
}): Promise<ApiSuccess<SendStats>> => {
  return axios.get(apiBase + "/sends/stats", { params });
};

export type SendStatsSeriesPoint = {
  date: string;
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  goal_count: number;
};

export type SendStatsSeries = {
  period_days: number;
  from: string;
  to: string;
  series: SendStatsSeriesPoint[];
};

/** Série diária UTC — GET /sends/stats/series (F2). */
export const getSendStatsSeries = (params?: {
  days?: number;
}): Promise<ApiSuccess<SendStatsSeries>> => {
  return axios.get(apiBase + "/sends/stats/series", { params });
};

/** Melhor horário de abertura (UTC → hint local) — GET /sends/stats/best-hours. */
export type BestOpenHours = {
  period_days: number;
  sample_opens: number;
  hours: Array<{ hour: number; opens: number }>;
  best_hour: number | null;
  best_hour_local_hint: string | null;
};

export const getBestOpenHours = (params?: {
  days?: number;
}): Promise<ApiSuccess<BestOpenHours>> => {
  return axios.get(apiBase + "/sends/stats/best-hours", { params });
};

/** Marca/desmarca link meta — PATCH /sends/{id}/links/{link}. */
export const updateSendLinkGoal = (
  sendId: number,
  linkId: number,
  isGoal: boolean
): Promise<ApiSuccess<CampaignSend>> => {
  return axios.patch(`${apiBase}/sends/${sendId}/links/${linkId}`, {
    is_goal: isGoal,
  });
};

type RecipientsListResponse = ApiSuccess<CampaignSendRecipient[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

/** Drill-down de destinatários — GET /sends/{id}/recipients. */
export const getSendRecipients = (
  sendId: number,
  params?: {
    status?: CampaignSendRecipientStatus | "";
    engagement?: RecipientEngagementFilter;
    bounce_kind?: BounceKindFilter;
    page?: number;
    per_page?: number;
  }
): Promise<RecipientsListResponse> => {
  return axios.get(apiBase + "/sends/" + sendId + "/recipients", { params });
};

/** Atribui tag (append) aos destinatários filtrados — POST …/recipients/tag. */
export const tagSendRecipients = (
  sendId: number,
  payload: {
    tag_id: number;
    status?: CampaignSendRecipientStatus | "";
    engagement?: RecipientEngagementFilter;
    bounce_kind?: BounceKindFilter;
  }
): Promise<ApiSuccess<{ tagged: number; tag_id: number }>> => {
  return axios.post(apiBase + "/sends/" + sendId + "/recipients/tag", payload);
};

/**
 * Formata taxa da API (% 0–100) para exibição — ex.: "25,5%".
 * Valores nulos/NaN → travessão.
 */
export const formatRatePercent = (
  value: number | null | undefined,
  locale?: string
): string => {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  const n = Number(value);
  return (
    n.toLocaleString(locale || undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }) + "%"
  );
};

/** Limiar amigável Carla: bounce ≥ N% merece atenção (default 5 — LAC-191). */
export const BOUNCE_RATE_WARN_THRESHOLD = 5;

export const clampBounceWarnThreshold = (
  value: number | null | undefined
): number => {
  if (value == null || Number.isNaN(Number(value))) {
    return BOUNCE_RATE_WARN_THRESHOLD;
  }
  return Math.max(1, Math.min(20, Math.round(Number(value))));
};

export const isBounceRateHigh = (
  rate: number | null | undefined,
  threshold: number = BOUNCE_RATE_WARN_THRESHOLD
): boolean =>
  rate != null &&
  !Number.isNaN(Number(rate)) &&
  Number(rate) >= clampBounceWarnThreshold(threshold);

/**
 * Baixa CSV no browser a partir de linhas já carregadas (F1).
 * Export assíncrono de listas grandes = LAC futuro.
 * `labels` troca hard/soft por copy leiga (Inválido/Temporário) na coluna bounce_kind.
 */
export const downloadRecipientsCsv = (
  rows: CampaignSendRecipient[],
  filename: string,
  labels?: { hard: string; soft: string }
): void => {
  const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
  const bounceLabel = (kind: BounceKind | null | undefined): string => {
    if (kind === "hard") return labels?.hard ?? kind;
    if (kind === "soft") return labels?.soft ?? kind;
    return kind || "";
  };
  const header = [
    "email",
    "status",
    "bounce_kind",
    "opened",
    "clicked",
    "opened_at",
    "clicked_at",
  ];
  const lines = [
    header.join(","),
    ...rows.map((r) =>
      [
        escape(r.email),
        escape(r.status),
        escape(bounceLabel(r.bounce_kind)),
        escape(r.opened ? "1" : "0"),
        escape(r.clicked ? "1" : "0"),
        escape(r.opened_at || ""),
        escape(r.clicked_at || ""),
      ].join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/** Disparo oficial — 202 + id (POST /campaigns/{id}/send). */
export const queueCampaignSend = (
  campaignId: number,
  payload: SendCampaignPayload
): Promise<QueueSendResponse> => {
  return axios.post(apiBase + "/campaigns/" + campaignId + "/send", payload);
};

/** Contagem estimada da audiência elegível. */
export const estimateCampaignAudience = (
  campaignId: number,
  payload: Pick<SendCampaignPayload, "audience">
): Promise<ApiSuccess<{ estimated: number }>> => {
  return axios.post(
    apiBase + "/campaigns/" + campaignId + "/send/estimate",
    payload
  );
};

/** Envio de teste — 202; não consome cota (POST /campaigns/{id}/test). */
export const queueCampaignTest = (
  campaignId: number,
  payload: TestCampaignPayload
): Promise<QueueSendResponse> => {
  return axios.post(apiBase + "/campaigns/" + campaignId + "/test", payload);
};

export const cancelCampaignSend = (
  sendId: number
): Promise<ApiSuccess<CampaignSend>> => {
  return axios.post(apiBase + "/sends/" + sendId + "/cancel");
};

/** Agenda o restante da amostra parcial (tag dos excluídos) — LAC-115. */
export const queuePartialRemainder = (
  sendId: number,
  payload?: { scheduled_at?: string | null }
): Promise<QueueSendResponse> => {
  return axios.post(apiBase + "/sends/" + sendId + "/queue-remainder", payload ?? {});
};

/** Cancela a cadeia parcial (pai + continuações). */
export const cancelPartialChain = (
  sendId: number
): Promise<ApiSuccess<{ cancelled: number[]; skipped: number[] }>> => {
  return axios.post(apiBase + "/sends/" + sendId + "/cancel-chain");
};

/** Pausa a cadeia parcial (retomável). */
export const pausePartialChain = (
  sendId: number
): Promise<ApiSuccess<{ paused: number[]; skipped: number[] }>> => {
  return axios.post(apiBase + "/sends/" + sendId + "/pause-chain");
};

/** Retoma a cadeia parcial pausada. */
export const resumePartialChain = (
  sendId: number
): Promise<ApiSuccess<{ resumed: number[]; skipped: number[] }>> => {
  return axios.post(apiBase + "/sends/" + sendId + "/resume-chain");
};

/** Baixa CSV dos contatos fora da amostra. */
export const downloadExcludedContactsCsv = async (sendId: number): Promise<void> => {
  try {
    const res = await axios.get(apiBase + `/sends/${sendId}/excluded.csv`, {
      responseType: "blob",
    });
    const contentType = String(res.headers?.["content-type"] || "");
    // Envelope JSON de erro às vezes chega como blob.
    if (contentType.includes("application/json")) {
      const text = await (res.data as Blob).text();
      let message = "";
      try {
        const parsed = JSON.parse(text) as {
          error?: { message?: string };
          message?: string;
        };
        message = parsed?.error?.message || parsed?.message || "";
      } catch {
        message = "";
      }
      const err = new Error(message || "PARTIAL_EXCLUDED_TAG_MISSING") as Error & {
        code?: string;
      };
      err.code = "PARTIAL_EXCLUDED_TAG_MISSING";
      throw err;
    }
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
    if (blob.size < 40) {
      const err = new Error("PARTIAL_EXCLUDED_EMPTY") as Error & { code?: string };
      err.code = "PARTIAL_EXCLUDED_EMPTY";
      throw err;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `partial-excluded-send-${sendId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e: unknown) {
    const ax = e as {
      code?: string;
      message?: string;
      response?: { data?: Blob | { error?: { message?: string } } };
    };
    if (ax?.code === "PARTIAL_EXCLUDED_TAG_MISSING" || ax?.code === "PARTIAL_EXCLUDED_EMPTY") {
      throw ax;
    }
    const data = ax?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text) as { error?: { message?: string; code?: string } };
        const err = new Error(
          parsed?.error?.message || ax.message || "CSV_ERROR"
        ) as Error & { code?: string };
        err.code = parsed?.error?.code || "CSV_ERROR";
        throw err;
      } catch (inner) {
        if (inner instanceof Error && (inner as Error & { code?: string }).code) {
          throw inner;
        }
      }
    }
    throw e;
  }
};

export const approveCampaignSend = (
  sendId: number
): Promise<ApiSuccess<CampaignSend>> =>
  axios.post(`${apiBase}/sends/${sendId}/approve`);

export const rejectCampaignSend = (
  sendId: number,
  reason?: string
): Promise<ApiSuccess<CampaignSend>> =>
  axios.post(`${apiBase}/sends/${sendId}/reject`, {
    reason: reason?.trim() || undefined,
  });

export const isSendCancellable = (status: CampaignSendStatus): boolean =>
  status === "queued" || status === "preparing" || status === "sending";

export const isSendInProgress = (status: CampaignSendStatus): boolean =>
  status === "queued" || status === "preparing" || status === "sending";
